import { motion } from 'framer-motion';
import { useUser } from '../contexts/UserContext';

// MIMO SVG Character - Minimalist soft 3D style
const MimoSVG = ({ mood = 'happy', energyLevel = 50, style = 'elegant' }) => {
  const moodColors = {
    happy: '#FFD166',
    sad: '#4F8CFF',
    calm: '#06D6A0',
    excited: '#FF7A59',
    neutral: '#B794F4'
  };

  const glowColor = moodColors[mood] || moodColors.neutral;
  
  // Eye expressions based on mood
  const getEyeExpression = () => {
    switch (mood) {
      case 'happy':
        return { eyeHeight: 14, eyeY: 38, pupilY: 40 };
      case 'sad':
        return { eyeHeight: 10, eyeY: 42, pupilY: 44 };
      case 'excited':
        return { eyeHeight: 16, eyeY: 36, pupilY: 38 };
      case 'calm':
        return { eyeHeight: 12, eyeY: 40, pupilY: 42 };
      default:
        return { eyeHeight: 13, eyeY: 39, pupilY: 41 };
    }
  };

  const eyeExpr = getEyeExpression();

  // Mouth expression
  const getMouth = () => {
    switch (mood) {
      case 'happy':
        return <path d="M40 60 Q50 70 60 60" stroke="#1C1917" strokeWidth="3" fill="none" strokeLinecap="round" />;
      case 'sad':
        return <path d="M40 65 Q50 58 60 65" stroke="#1C1917" strokeWidth="3" fill="none" strokeLinecap="round" />;
      case 'excited':
        return <ellipse cx="50" cy="62" rx="8" ry="6" fill="#1C1917" />;
      case 'calm':
        return <line x1="42" y1="62" x2="58" y2="62" stroke="#1C1917" strokeWidth="3" strokeLinecap="round" />;
      default:
        return <path d="M42 60 Q50 64 58 60" stroke="#1C1917" strokeWidth="2.5" fill="none" strokeLinecap="round" />;
    }
  };

  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <defs>
        {/* Glow filter */}
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        
        {/* Gradient for body */}
        <radialGradient id="bodyGradient" cx="40%" cy="30%">
          <stop offset="0%" stopColor="#FAFAFA" />
          <stop offset="100%" stopColor="#E8E4E0" />
        </radialGradient>
        
        {/* Inner glow */}
        <radialGradient id="innerGlow" cx="50%" cy="50%">
          <stop offset="0%" stopColor={glowColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={glowColor} stopOpacity="0" />
        </radialGradient>
      </defs>
      
      {/* Aura glow based on energy */}
      <circle 
        cx="50" 
        cy="50" 
        r={35 + (energyLevel / 10)} 
        fill="url(#innerGlow)"
        className="animate-pulse-glow"
      />
      
      {/* Main body - soft rounded shape */}
      <ellipse 
        cx="50" 
        cy="52" 
        rx="32" 
        ry="30"
        fill="url(#bodyGradient)"
        filter="url(#glow)"
      />
      
      {/* Subtle body highlight */}
      <ellipse 
        cx="42" 
        cy="42" 
        rx="12" 
        ry="8"
        fill="white"
        opacity="0.6"
      />
      
      {/* Left eye */}
      <ellipse 
        cx="38" 
        cy={eyeExpr.eyeY} 
        rx="8" 
        ry={eyeExpr.eyeHeight}
        fill="white"
      />
      <circle cx="38" cy={eyeExpr.pupilY} r="5" fill="#1C1917" />
      <circle cx="36" cy={eyeExpr.pupilY - 2} r="2" fill="white" />
      
      {/* Right eye */}
      <ellipse 
        cx="62" 
        cy={eyeExpr.eyeY} 
        rx="8" 
        ry={eyeExpr.eyeHeight}
        fill="white"
      />
      <circle cx="62" cy={eyeExpr.pupilY} r="5" fill="#1C1917" />
      <circle cx="60" cy={eyeExpr.pupilY - 2} r="2" fill="white" />
      
      {/* Mouth */}
      {getMouth()}
      
      {/* Blush marks for happy/excited */}
      {(mood === 'happy' || mood === 'excited') && (
        <>
          <ellipse cx="26" cy="52" rx="5" ry="3" fill="#FFB4A2" opacity="0.6" />
          <ellipse cx="74" cy="52" rx="5" ry="3" fill="#FFB4A2" opacity="0.6" />
        </>
      )}
      
      {/* Small arms/nubs */}
      <ellipse cx="20" cy="55" rx="6" ry="8" fill="url(#bodyGradient)" />
      <ellipse cx="80" cy="55" rx="6" ry="8" fill="url(#bodyGradient)" />
    </svg>
  );
};

export const MimoCharacter = ({ 
  mood = 'happy', 
  size = 'lg',
  showAura = true,
  onClick 
}) => {
  const { user } = useUser();
  const energyLevel = user?.energy_level || 50;
  const ageMode = user?.age_mode || 'aliado';

  const sizeClasses = {
    sm: 'w-20 h-20',
    md: 'w-32 h-32',
    lg: 'w-48 h-48',
    xl: 'w-64 h-64'
  };

  return (
    <motion.div
      className={`relative ${sizeClasses[size]} cursor-pointer`}
      animate={{
        scale: [1, 1.03, 1],
        y: [0, -5, 0]
      }}
      transition={{
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      data-testid="mimo-character"
    >
      {/* Background aura */}
      {showAura && (
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(circle, rgba(255,122,89,${energyLevel / 200}) 0%, transparent 70%)`
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
      
      <MimoSVG mood={mood} energyLevel={energyLevel} style={ageMode} />
    </motion.div>
  );
};

export default MimoCharacter;
